import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Divider, Grid, IconButton } from "@material-ui/core";
import CustomerDocumentIcon from "@material-ui/icons/FolderShared";
import DownloadIcon from "@material-ui/icons/GetAppTwoTone";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import translate from "../i18n/Translator";
import Surface from "../components/Surface";
import { CustomerDocumentRequest, CustomerDocumentsRequest, CustomerDocuments } from "../model/CustomerDocument";
import { getCustomerDocument, uploadCustomerDocument, getCustomerDocumentUrl } from "../api/CustomerDocumentAPI";
import { getDocumentUrl } from "../api/DocumentAPI";
import { AppContext } from "../context/AppContext";
import UploaderInput from "../components/UploaderInput";
import { InputRef } from "../components/ValidatedInput";
import { SuccessSnackbar, ErrorSnackbar } from "../components/Snackbars";
import Alert from "../components/Alert";
import { APP_COLORS } from "../components/Colors";

export default function CustomerDocumentForm() {
    const context = useContext(AppContext);
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState<CustomerDocuments>();
    const [request, setRequest] = useState<CustomerDocumentsRequest>({} as CustomerDocumentsRequest);
    const [validations, setValidations] = useState({} as any);
    const [success, setSuccess] = useState<string>();
    const [error, setError] = useState<string>();

    useEffect(() => {


        getCustomerDocument(context.session!.tenant!.id, context.session!.customer!.id).then(data => {
            const promises = data.documents.filter(document => !!document.value).map(document => getCustomerDocumentUrl(context.session!.tenant!.id, context.session!.customer!.id, document.id));
            Promise.all(promises).then((urls => {
                const documents = data.documents.map((document, index) => {
                    return {
                        id: document.id,
                        name: document.name,
                        description: document.description,
                        url: urls[index]
                    } as CustomerDocumentRequest;
                });

                setData(data);
                setRequest({ ...request, documents: documents });
            }))


        }).then(error => {
        });
        // eslint-disable-next-line
    }, []);

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        if (request) {
            const index = request.documents.findIndex((el) => el.id === name);
            if (index >= 0) {
                request.documents[index].url = value;
                setRequest(request);
            }
        }

        validations[name] = inputRef;
        setValidations(validations);
    };

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!request) return;
        if (!isValid()) return;

        setSubmitting(true);
        uploadCustomerDocument(context.session!.tenant!.id, context.session!.customer!.id, request).then((response) => {
            setSuccess(translate("documents.uploader.success") as string);
            setData(response);
        }).catch(error => {
            setError(error.message);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const onDownloadDocument = (documentId: string) => {
        getDocumentUrl(context.session!.tenant!.id, documentId).then(url => {
            window.open(url, "_blank");
        }).catch((error) => {
            setError(error.message);
        });
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setError(undefined);
    };

    const getColor = (status: "PENDING" | "VALIDATING" | "COMPLETED" | "REFUSED") => {
        switch (status) {
            case "VALIDATING":
                return APP_COLORS.info;
            case "COMPLETED":
                return APP_COLORS.success;
            case "REFUSED":
                return APP_COLORS.error;
            default:
                return undefined;
        }
    };

    if (!data || !request.documents) {
        return (<></>);
    }

    if (data.status === "COMPLETED" || data.status === "VALIDATING") {
        return (
            <Grid container>
                <Alert variant={data.status === "VALIDATING" ? "info" : "success"} margin={0}
                    title={translate("documents.uploader.title")}
                    subheader={translate(`documents.status_desc.${data.status}`)}
                    button={
                        <IconButton component={Link} to="/documents">
                            <NavigateNextIcon />
                        </IconButton>
                    } />
            </Grid>
        );
    }

    return (
        <Surface title={translate("documents.uploader.title")}
            subtitle={translate(`documents.status_desc.${data.status}`)}
            icon={<CustomerDocumentIcon />} avatarColor={getColor(data.status)}>
            <Grid container justify="center">
                <Grid item xs={12} lg={8} xl={6}>
                    <form autoComplete="off" noValidate onSubmit={onSubmit} >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {request.documents.map((document, index) => (
                                    <Grid container key={document.id}>
                                        <Grid item xs={12}>
                                            <Grid container alignItems="flex-start" spacing={3}>
                                                <Grid item xs>
                                                    {document.description || translate("documents.uploader.instructions")}
                                                </Grid>
                                                <Grid item xs="auto">
                                                    <Button size="small" variant="text" startIcon={<DownloadIcon />} onClick={() => onDownloadDocument(document.id)}>
                                                        {translate("buttons.download")}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <UploaderInput id={document.id} name={document.id}
                                                url={document.url} label={document.name}
                                                required disabled={submitting}
                                                margin="dense" acceptExtension=".pdf,.doc,.docx"
                                                onValueChanged={hasChanged} />
                                        </Grid>
                                        {index < data.documents.length - 1 && (
                                            <Grid item xs={12}>
                                                <Box pb={1} pt={2}>
                                                    <Divider />
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container justify="flex-end">
                                    <Grid item xs="auto">
                                        <Button type="submit" variant="contained" color="primary" size="large" disabled={submitting}>
                                            {translate("buttons.update")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </form>
                </Grid>
            </Grid>
            <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
        </Surface>
    );

}